"use client";

import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence, useMotionValue, PanInfo, useTransform, useAnimation } from 'framer-motion';
import ProductCard from '@/components/products/product-card';
import type { Product } from '@/types/product';



interface ProductCarouselProps {
  products: Product[];
  title?: string;
  className?: string;
}

export default function ProductCarousel({
  products,
  title = "Recommended for You",
  className = ""
}: ProductCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();
  const [containerWidth, setContainerWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  // For drag/swipe functionality
  const x = useMotionValue(0);
  
  // Calculate how many products to show per slide based on screen size
  const getProductsPerSlide = () => {
    if (typeof window === 'undefined') return 4; // Default for SSR
    if (window.innerWidth < 640) return 1; // Mobile
    if (window.innerWidth < 768) return 2; // Small tablet
    if (window.innerWidth < 1024) return 3; // Large tablet
    return 4; // Desktop
  };
  
  const productsPerSlide = getProductsPerSlide();
  const totalSlides = Math.ceil(products.length / productsPerSlide);
  
  // Update container dimensions on mount and resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);
  
  // Update position when currentIndex changes
  useEffect(() => {
    controls.start({
      x: -currentIndex * containerWidth,
      transition: { type: "spring", stiffness: 300, damping: 30 }
    });
  }, [currentIndex, containerWidth, controls]);
  
  const handleDragEnd = (_e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setIsDragging(false);
    const threshold = containerWidth / 4; // 25% of container width
    
    if (Math.abs(info.offset.x) > threshold) {
      // If dragged far enough, switch slide
      if (info.offset.x > 0 && currentIndex > 0) {
        // Dragged right - go to previous slide
        prevSlide();
      } else if (info.offset.x < 0 && currentIndex < totalSlides - 1) {
        // Dragged left - go to next slide
        nextSlide();
      } else {
        // Bounce back if at the end
        controls.start({
          x: -currentIndex * containerWidth,
          transition: { type: "spring", stiffness: 300, damping: 30 }
        });
      }
    } else {
      // If not dragged far enough, snap back
      controls.start({
        x: -currentIndex * containerWidth,
        transition: { type: "spring", stiffness: 300, damping: 30 }
      });
    }
  };
  
  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === totalSlides - 1 ? 0 : prevIndex + 1
    );
  };
  
  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? totalSlides - 1 : prevIndex - 1
    );
  };
  
  // Format price nicely
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price);
  };
  
  return (
    <div className={`py-10 ${className}`}>
      {title && (
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">{title}</h2>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={prevSlide}
              className="rounded-full"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={nextSlide}
              className="rounded-full"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}
      
      <div 
        ref={containerRef}
        className="overflow-hidden relative cursor-grab"
      >
        <motion.div
          className="w-full" 
          animate={controls}
          drag="x"
          dragConstraints={{ left: -containerWidth * (totalSlides - 1), right: 0 }}
          dragElastic={0.1}
          dragMomentum={true}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={handleDragEnd}
          style={{ touchAction: 'pan-y' }}
        >
          <div className="flex">
            {Array.from({ length: totalSlides }).map((_, slideIndex) => (
              <div 
                key={slideIndex} 
                className="min-w-full w-full"
                style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 px-1">
                  {products
                    .slice(
                      slideIndex * productsPerSlide, 
                      (slideIndex * productsPerSlide) + productsPerSlide
                    )
                    .map(product => (
                      <div key={product.id} className="h-full">
                        <ProductCard product={{
                          id: product.id,
                          name: product.name,
                          price: product.price,
                          slug: product.slug,
                          image_url: product.image_url || '/images/product-placeholder.png',
                          description: product.description || '',
                          stock: product.stock || 10,
                          has_variants: product.has_variants || false // Default to in stock if not specified
                        }} />
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
      
      {/* Slide indicators */}
      <div className="flex justify-center mt-4 gap-1">
        {Array.from({ length: totalSlides }).map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full ${
              index === currentIndex ? 'bg-primary' : 'bg-muted'
            }`}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </div>
    </div>
  );
}
